import { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, Route, Routes, useNavigate, useParams, useSearchParams } from "react-router-dom";
import type { ReactNode } from "react";

import AuthGate from "../components/AuthGate";
import DashboardFilterBar from "../components/DashboardFilterBar";
import DashboardShell from "../components/DashboardShell";
import DataDelayBanner from "../components/DataDelayBanner";
import DrillDownModal from "../components/DrillDownModal";
import FirstRunOverlay from "../components/FirstRunOverlay";
import MeridianLockup from "../components/MeridianLockup";
import SectionHeader, { widgetSection } from "../components/SectionHeader";
import { sectionGroupKey, sectionLabel } from "../lib/sectionLabels";
import { columnsForWidth, planSectionRows } from "../lib/sectionGrid.mjs";
import ContainerInventoryWidget from "../components/ContainerInventoryWidget";
import WidgetRenderer, { LockedTab } from "../components/WidgetRenderer";
import StaffOnlyNotice from "../components/StaffOnlyNotice";
import RoEfficiencyPanel from "../components/RoEfficiencyPanel";
import EmptyState from "../components/states/EmptyState";
import Skeleton from "../components/states/Skeleton";
import AuditFindings from "./AuditFindings";
import Executive from "./Executive";
import HelpRequests from "./HelpRequests";
import Insights from "./Insights";
import Settings from "./Settings";
import VarianceRegister from "./VarianceRegister";

import {
  effectiveChartType,
  looksLikeSpaFallback,
  ManifestParseError,
  parseManifest,
  widgetsForTab,
  type DashboardManifest,
  type ManifestWidget,
} from "../lib/manifest";
import {
  isOnboardingDismissed,
  markOnboardingDismissed,
  useHasCanonicalData,
} from "../lib/emptyData";
import RateInputPanel from "../components/RateInputPanel";
import { ExecKpiProvider, useExecKpiAvailability } from "../lib/execKpiData";
import { FeedLagProvider } from "../lib/feedLagData";
import { FlagMapProvider } from "../lib/flagContext";
import { sectionProvenance } from "../lib/sectionProvenance.mjs";
import { widgetDomain } from "../lib/widgetDomain.mjs";
import {
  DRILL_FOCUS_PARAM,
  drillHref,
  resolveDrillTarget,
  tabIsUnlocked,
  type DrillTarget,
} from "../lib/drillTarget.mjs";
import { helpDeskVisible } from "../lib/helpDesk/helpDesk.mjs";
import { executiveTabId, resolveIndexRoute } from "../lib/navSections.mjs";
import { useFlagStream } from "../lib/flagStream";
import { applyGlobalFilters, useGlobalFilters } from "../lib/globalFilters";
import { isStaff, useAuth } from "../lib/auth";
import { track } from "../lib/telemetry";
import { useTenantId } from "../lib/tenant";
import { loadPrefs } from "../lib/userPreferences";
import { ReportPage, reportComponent } from "../reports";
import { entityForReport, entityForTabRoute } from "../lib/tabFreshness.mjs";
import { useTabUpdatedAt } from "../lib/tabFreshnessData";

/**
 * Per-customer dashboard shell (MER-115 Phase 5).
 *
 * Routes:
 *   /:slug              -> redirect to first tab
 *   /:slug/:tabId       -> render the tab via WidgetRenderer grid
 *
 * Every per-customer route is wrapped in AuthGate. The shell renders
 * the header + sidebar (DashboardShell); the content area swaps as
 * the user navigates tabs.
 */

// MER-625: tabIsUnlocked moved to drillTarget.mjs so the tier lock on this
// page and the gate that decides whether a drill may open a tab read one
// rule. A drill into a locked tab would land the reader on the upgrade
// notice instead of the detail it promised.

/**
 * MER-162: bucket widgets into ordered sections by parsed id prefix.
 * Preserves first-appearance order (so manifest sort wins). Returns a
 * Map<groupKey, widgets[]> so a single iteration of `.entries()`
 * yields sections in their canonical render order.
 *
 * MER-199 to MER-210: each raw `widgetSection()` output is first
 * passed through `sectionGroupKey(tabId, raw)` so per-tab merges
 * (e.g. L6c.1/2/3/4 -> "exec-headline") collapse to a single bucket.
 */
function groupBySection(
  widgets: ManifestWidget[],
  tabId: string | undefined,
): Map<string, ManifestWidget[]> {
  const groups = new Map<string, ManifestWidget[]>();
  for (const w of widgets) {
    const raw = widgetSection(w.id);
    const key = sectionGroupKey(tabId, raw);
    const bucket = groups.get(key);
    if (bucket) bucket.push(w);
    else groups.set(key, [w]);
  }
  return groups;
}

/**
 * MER-214: discriminate manifest load failures so the client never
 * sees a raw exception string. `not_configured` means "no manifest
 * bundled for this slug yet" (new customer onboarding); `config_error`
 * means "manifest exists but is corrupt or unreachable". Each maps to
 * a brand-aligned card in the renderer below.
 */
type ManifestState =
  | { kind: "loading" }
  | { kind: "ready"; manifest: DashboardManifest }
  | { kind: "not_configured" }
  | { kind: "config_error" };

export default function CustomerDashboard() {
  const { slug } = useParams<{ slug: string }>();
  const [manifestState, setManifestState] = useState<ManifestState>({ kind: "loading" });

  // MER-164: all hooks must precede the conditional early returns below
  // (rules of hooks). The early returns gate the children of this
  // component, but hook order has to be identical on every render.
  const { session: settingsSession } = useAuth();
  const userId = settingsSession?.user.id ?? null;
  // MER-688: staff (impersonating or not) reach the staff_only tabs and the
  // Insights / Findings routes; a client on those URLs gets the staff-only
  // notice below. Same claim read as StaffGate. Read once here and threaded
  // into the flag poller, the routes and the landing-tab guard.
  const staffSession = isStaff(settingsSession);

  // MER-167: poll for new RED flags + surface a toast when count
  // grows since dashboard mount. Hook is no-op when slug undefined.
  // MER-688: a client's toast carries no Findings action.
  useFlagStream(slug, { isStaff: staffSession });

  // MER-576: the tenant's FLAG rows, fetched ONCE for the whole page and
  // handed to every widget through context. Painting Overview already costs
  // 46 requests with no caching layer, so a per-tile flag query would have
  // been the worst possible way to add colour.
  const flagTenantId = useTenantId();

  // MER-171: detect zero canonical data + show onboarding overlay for
  // fresh tenants. `null` while in-flight; only render the overlay
  // when we have a confirmed-empty answer.
  const hasData = useHasCanonicalData(slug);
  const [overlayDismissed, setOverlayDismissed] = useState<boolean>(() =>
    slug ? isOnboardingDismissed(slug) : false,
  );
  useEffect(() => {
    if (slug) setOverlayDismissed(isOnboardingDismissed(slug));
  }, [slug]);
  const showFirstRunOverlay = hasData === false && !overlayDismissed && slug;

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setManifestState({ kind: "loading" });
    fetch(`/manifests/${slug}.yaml`)
      .then(async (resp) => {
        if (cancelled) return;
        // MER-214 failure-mode routing:
        // * 404 => Cloudflare did NOT serve the SPA fallback; the slug
        //   has no bundled manifest. Render NotConfigured.
        // * SPA fallback (200 with index.html body) => same outcome as
        //   404 but Pages chose to serve index.html. Detect via Content
        //   -Type or body sniff.
        // * 2xx non-html => parse. ManifestParseError => ConfigurationError.
        // * Other status => ConfigurationError.
        if (resp.status === 404) {
          setManifestState({ kind: "not_configured" });
          return;
        }
        if (!resp.ok) {
          // eslint-disable-next-line no-console
          console.error(
            `Manifest fetch failed for slug=${slug}: HTTP ${resp.status} ${resp.statusText}`,
          );
          setManifestState({ kind: "config_error" });
          return;
        }
        const contentType = resp.headers.get("content-type");
        const text = await resp.text();
        if (looksLikeSpaFallback(contentType, text)) {
          setManifestState({ kind: "not_configured" });
          return;
        }
        try {
          const parsed = parseManifest(text);
          if (cancelled) return;
          setManifestState({ kind: "ready", manifest: parsed });
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error(
            `Manifest parse failed for slug=${slug}:`,
            err instanceof ManifestParseError && err.cause ? err.cause : err,
          );
          setManifestState({ kind: "config_error" });
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // Network-level failure: no Cloudflare response at all. Log for
        // engineering; render ConfigurationError so the customer sees a
        // brand-aligned card with a support link.
        // eslint-disable-next-line no-console
        console.error(`Manifest fetch network error for slug=${slug}:`, err);
        setManifestState({ kind: "config_error" });
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const savedPrefs = userId && slug ? loadPrefs(userId, slug) : null;
  const [searchParams, setSearchParams] = useSearchParams();
  const appliedPeriodRef = useRef(false);
  useEffect(() => {
    if (appliedPeriodRef.current) return;
    if (!savedPrefs?.defaultPeriod) return;
    if (searchParams.get("period")) return;
    const next = new URLSearchParams(searchParams);
    next.set("period", savedPrefs.defaultPeriod);
    setSearchParams(next, { replace: true });
    appliedPeriodRef.current = true;
  }, [savedPrefs, searchParams, setSearchParams]);

  if (!slug) {
    return (
      <AuthGate>
        <FallbackNotice message="No customer slug in URL." />
      </AuthGate>
    );
  }

  // MER-214: distinguish manifest load failure modes. The renderer
  // never echoes a raw exception to the client.
  if (manifestState.kind === "not_configured") {
    return (
      <AuthGate>
        <NotConfiguredCard slug={slug} />
      </AuthGate>
    );
  }
  if (manifestState.kind === "config_error") {
    return (
      <AuthGate>
        <ConfigurationErrorCard slug={slug} />
      </AuthGate>
    );
  }
  if (manifestState.kind === "loading") {
    return (
      <AuthGate>
        <main style={{ padding: 32 }}>
          <Skeleton label="Loading manifest" height={120} />
        </main>
      </AuthGate>
    );
  }

  // Derivations below all need manifest; safe now that we're past the
  // early-return guard.
  const manifest = manifestState.manifest;
  // AJ 2026-09-28: the manifest tab this tenant's override made its Executive
  // view (Best Disposal's Board), if any. /<slug>/Executive redirects to it.
  const execTabId = executiveTabId(manifest.tabs, { isStaff: staffSession });
  // MER-688: the landing tab is resolved through the same gate as the
  // sidebar and the Settings option list. A saved preference that names a
  // staff_only tab (stale, or saved by a staff user on this browser) falls
  // back to the first tab this session can see instead of redirecting a
  // client to the staff-only wall on every sign-in.

  function handleDismissOverlay() {
    if (!slug) return;
    markOnboardingDismissed(slug);
    setOverlayDismissed(true);
  }

  return (
    <AuthGate>
      <FlagMapProvider tenantId={flagTenantId}>
        {/* MER-693: one "data through" probe per source per page, shared by
            every widget that reads that source. */}
        <FeedLagProvider tenantId={flagTenantId}>
          {/* MER-674: one exec_kpi probe per tenant, shared by the shell (the
            Executive nav link) and the Executive page. */}
          <ExecKpiProvider tenantId={flagTenantId}>
            <DashboardShell manifest={manifest}>
              {showFirstRunOverlay ? (
                <FirstRunOverlay slug={slug} onDismiss={handleDismissOverlay} />
              ) : null}
              <Routes>
                <Route
                  index
                  element={
                    <IndexRedirect
                      tabs={manifest.tabs}
                      preferred={savedPrefs?.defaultLandingTab}
                      isStaff={staffSession}
                      hasTenant={Boolean(flagTenantId)}
                      derivedExecutive={manifest.executive_page === "derived"}
                    />
                  }
                />
                {/* MER-674: the Executive board page. Bespoke like Insights (no
                manifest tab), but CLIENT-visible and not tier-locked: it is
                the client's own workbook. The nav only links it when the
                tenant has exec_kpi rows; a direct visit without them renders
                the awaiting card. AJ 2026-09-28: a tenant whose override marks
                a manifest tab `executive: true` (Best Disposal's Board) has
                that tab as its Executive, so this URL redirects there. */}
                <Route
                  path="Executive"
                  element={
                    execTabId ? (
                      <Navigate to={`../${execTabId}`} replace />
                    ) : (
                      <>
                        {/* 2026-10-05: every scheduled feed the board draws on. */}
                        <DataDelayBanner schedule={manifest.data_schedule} />
                        <Executive
                          divisions={manifest.divisions}
                          source={manifest.executive_page === "derived" ? "derived" : "workbook"}
                        />
                      </>
                    )
                  }
                />
                {/* Built-in routes outside the manifest tab loop. MER-688:
                Insights + Findings are Meridian-internal; a client session
                sees the staff-only notice, never the page and never a 404. */}
                <Route
                  path="Insights"
                  element={
                    staffSession ? (
                      <Insights />
                    ) : (
                      <LockedTabPage manifest={manifest} slug={slug}>
                        <StaffOnlyNotice tabLabel="Insights" />
                      </LockedTabPage>
                    )
                  }
                />
                <Route
                  path="Findings"
                  element={
                    staffSession ? (
                      <AuditFindings />
                    ) : (
                      <LockedTabPage manifest={manifest} slug={slug}>
                        <StaffOnlyNotice tabLabel="Findings" />
                      </LockedTabPage>
                    )
                  }
                />
                {/* MER-706: the staff variance register (public.board_variance,
                staff-read only). Internal like Findings: a client session on
                the URL gets the staff-only notice, never the page. */}
                <Route
                  path="Variance"
                  element={
                    staffSession ? (
                      <VarianceRegister derivedExecutive={manifest.executive_page === "derived"} />
                    ) : (
                      <LockedTabPage manifest={manifest} slug={slug}>
                        <StaffOnlyNotice tabLabel="Variance Register" />
                      </LockedTabPage>
                    )
                  }
                />
                {/* Settings has tabs (lib/settingsNav.mjs): /Settings is
                Preferences, /Settings/Inputs the customer's typed inputs. */}
                <Route path="Settings/*" element={<Settings />} />
                {/* Issue #1096: My requests (the help desk). Before :tabId so a
                    tab can never shadow it; hidden unless the help desk is on
                    for the tenant or the session is staff. */}
                <Route
                  path="Help"
                  element={
                    helpDeskVisible(manifest, { isStaff: staffSession }) ? (
                      <HelpRequests />
                    ) : (
                      <Navigate to={`/${slug}`} replace />
                    )
                  }
                />
                <Route
                  path=":tabId"
                  element={<TabRoute manifest={manifest} isStaff={staffSession} />}
                />
                <Route
                  path="*"
                  element={
                    <IndexRedirect
                      tabs={manifest.tabs}
                      preferred={savedPrefs?.defaultLandingTab}
                      isStaff={staffSession}
                      hasTenant={Boolean(flagTenantId)}
                    />
                  }
                />
              </Routes>
            </DashboardShell>
          </ExecKpiProvider>
        </FeedLagProvider>
      </FlagMapProvider>
    </AuthGate>
  );
}

/**
 * MER-528 / MER-536: subscribe to the active column count. The breakpoint
 * bands (`columnsForWidth`) and the per-row span math (`planSectionRows`,
 * `responsiveSpan`, `distributeRowSpans`) live in `lib/sectionGrid` so
 * `node --test` exercises them directly; this hook just tracks viewport
 * resizes and re-renders when the band changes.
 */
// MER-588: which tab carries the operator rate inputs. A constant rather than
// an inline string so the guard test can assert the mount without matching
// prose, and so moving the panel is one edit.
const RATE_PANEL_TAB = "Cost & Disposal";

function useGridColumns(): number {
  const [cols, setCols] = useState(() =>
    typeof window !== "undefined" ? columnsForWidth(window.innerWidth) : 12,
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () =>
      setCols((prev) => {
        const next = columnsForWidth(window.innerWidth);
        return next === prev ? prev : next;
      });
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return cols;
}

function TabRoute(props: { manifest: DashboardManifest; isStaff: boolean }) {
  const { manifest, isStaff: staffSession } = props;
  const { tabId } = useParams<{ tabId: string }>();
  const { slug } = useParams<{ slug: string }>();
  const [drillWidget, setDrillWidget] = useState<ManifestWidget | null>(null);
  const gridCols = useGridColumns();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // MER-625: the widget this tab should scroll to and ring, set by a drill
  // from another tab. Null on a normal tab open.
  const focusWidgetId = searchParams.get(DRILL_FOCUS_PARAM);
  // MER-588: the rate panel writes per-tenant rows, so it needs the resolved
  // tenant id. Null while auth is still resolving; the panel disables its save
  // button rather than writing an unscoped row.
  const tenantId = useTenantId();

  const tab = useMemo(
    () => manifest.tabs?.find((t) => (t.id ?? t.label) === tabId),
    [manifest.tabs, tabId],
  );

  // MER-688: every hook runs before the lock early-returns below. TabRoute
  // stays mounted across tab changes (same Route element, only :tabId
  // moves), so a hook that only ran on unlocked tabs would change the hook
  // count between a locked and an unlocked render.
  const globalFilters = useGlobalFilters();
  // AJ 2026-09-30: the tab's "Updated @" stamp (a manifest-driven tab's hero;
  // a report port reads the same cached stamp for its own top bar).
  const updated = useTabUpdatedAt(tenantId, entityForTabRoute(manifest, tabId));
  const widgets = useMemo(() => {
    // MER-306 close: skip widgets whose `service_gate` declares a LOB
    // the customer does not surface. The template ships one P&L card
    // per LOB (Front Load / Roll Off / Side Load) and one customer
    // (BD) has zero ASL revenue. Without the gate the Side Load
    // tile renders bare n/a; with the gate it is suppressed
    // entirely so the customer never sees a dead-data card.
    const enabled = new Set(manifest.enabled_services ?? []);
    return widgetsForTab(manifest, tabId ?? "")
      .filter((w) => effectiveChartType(w) !== undefined)
      .filter((w) => !w.service_gate || enabled.has(w.service_gate))
      .map((w) =>
        applyGlobalFilters(w, {
          period: globalFilters.period,
          periodExplicit: globalFilters.periodExplicit,
          range: globalFilters.range,
          rangeExplicit: globalFilters.rangeExplicit,
          // MER-692: an explicit calendar month from ?range=YYYY-MM.
          month: globalFilters.month,
          divisions: globalFilters.divisions,
          services: globalFilters.services,
          // MER-694: labels a scoped tile ("Roll Off only") on the default
          // load too, when every enabled line is in view.
          enabledServices: manifest.enabled_services ?? [],
        }),
      );
  }, [
    manifest,
    tabId,
    globalFilters.period,
    globalFilters.periodExplicit,
    globalFilters.range,
    globalFilters.rangeExplicit,
    globalFilters.month,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    globalFilters.divisions.join(","),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    globalFilters.services.join(","),
  ]);

  // MER-625: the drill target for each tile on this tab, resolved once.
  // Doubles as the affordance source: a tile with an entry here says where
  // the click goes on its face, so the reader knows before spending it
  // whether they get the Operations detail or a row dump.
  const drillTargets = useMemo(() => {
    const map = new Map<string, DrillTarget>();
    for (const w of widgets) {
      const target = resolveDrillTarget(manifest, w, { isStaff: staffSession });
      if (target) map.set(w.id, target);
    }
    return map;
  }, [manifest, widgets, staffSession]);

  // MER-625: land a drill on its target. The tab renders exactly as it does
  // on a normal open; this only brings the named tile into view and moves
  // keyboard focus onto it, so a reader who pressed Enter on an Overview tile
  // does not arrive at the top of a 42-tile Operations page hunting for the
  // number they clicked. Nothing here changes what any tile queries.
  useEffect(() => {
    if (!focusWidgetId || widgets.length === 0) return;
    if (typeof document === "undefined") return;
    // Quoted attribute selector: widget ids carry dots (1.1.3) and dashes
    // (L6c.7-trend), so only a backslash or a quote needs escaping.
    const escaped = focusWidgetId.replace(/[\\"]/g, "\\$&");
    const el = document.querySelector<HTMLElement>(`[data-widget-id="${escaped}"]`);
    if (!el) return;
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    el.focus({ preventScroll: true });
  }, [focusWidgetId, tabId, widgets.length]);

  /**
   * MER-625: what a tile click does.
   *
   * A tile that declares a drill target the customer actually has NAVIGATES
   * to the tab that owns the detail, carrying the page's filter state in the
   * URL so the target tile opens under the same window and scope. Every other
   * tile keeps the row-level drill-down modal it has had since MER-115.
   *
   * The two are not alternatives dressed the same way: the drill answers
   * "where does this number get broken down", the modal answers "which rows
   * add up to it". Only the first is Chris's ask, and only the pairs whose
   * bindings are verified identical get it (see drillTarget.mjs).
   *
   * Gated and empty tiles never reach here at all: WidgetRenderer withholds
   * the click affordance from them (MER-571).
   */
  const openWidget = (w: ManifestWidget) => {
    const target = slug ? drillTargets.get(w.id) : undefined;
    if (target && slug) {
      // MER-208: still a widget_drill. The reader drilled; which surface
      // answered is a payload detail, so MER-211's engagement counts stay
      // whole across both kinds.
      track("widget_drill", {
        widget_id: w.id,
        widget_name: w.name,
        drill_to: target.widgetId,
        drill_tab: target.tab,
      });
      navigate(drillHref(slug, target, searchParams));
      return;
    }
    setDrillWidget(w);
  };

  if (!tabId) {
    return <FallbackNotice message="No tab id in URL." />;
  }

  // MER-688: a staff_only tab reached by a client session (direct URL; the
  // nav never links it) renders the staff-only notice. Checked before the
  // tier lock so the copy says why the tab is closed, not what to buy.
  if (tab?.staff_only === true && !staffSession) {
    return (
      <LockedTabPage manifest={manifest} slug={slug}>
        <StaffOnlyNotice tabLabel={tab.label ?? tabId} />
      </LockedTabPage>
    );
  }

  if (!tabIsUnlocked(tab, manifest.tier)) {
    return (
      <LockedTabPage manifest={manifest} slug={slug}>
        <LockedTab
          tabLabel={tab?.label ?? tabId}
          requiredTier={(tab?.tier ?? "professional") as "professional" | "enterprise"}
        />
      </LockedTabPage>
    );
  }

  // AJ 2026-09-28: a tab that is one of the client's own reports renders its
  // React port (src/reports/README.md) in place of the hero, the filter bar
  // and the section grid. The page carries its own top bar, tabs and filters,
  // as the client's page does; our shell (header, nav) stays above it. An id
  // with no port yet falls through to the grid, so nothing changes until the
  // port lands.
  const Report = tab ? reportComponent(tab.report) : null;
  if (tab && Report) {
    return (
      <main style={{ padding: 0 }}>
        {/* 2026-10-05: the report's own feed is later than its schedule allows. */}
        <DataDelayBanner
          schedule={manifest.data_schedule}
          entities={[entityForReport(tab.report)]}
        />
        <ReportPage
          component={Report}
          tab={tab}
          manifest={manifest}
          widgets={widgets}
          tenantId={tenantId}
        />
      </main>
    );
  }

  return (
    <main style={{ padding: "24px 32px" }}>
      {/* MER-153: brand-mint -> white gradient hero per
          Branding/meridian_self_host_wrapper_mock.html .spec block.
          Layer eyebrow + tab title inside, brand-line border. */}
      <header
        style={{
          padding: "22px 28px",
          background: "linear-gradient(110deg, var(--brand-mint) 0%, #E6F3EE 50%, #FFFFFF 100%)",
          border: "1px solid var(--brand-line)",
          borderRadius: 8,
          marginBottom: 18,
          position: "relative",
          overflow: "hidden",
          // AJ 2026-09-30: title left, "Updated @" chip right (the Executive
          // page header's layout); wraps under the title when narrow.
          display: "flex",
          alignItems: "flex-end",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: 1,
              color: "var(--text-2)",
              fontWeight: 700,
            }}
          >
            {tab?.layer ?? "Layer"}
          </div>
          <h1
            style={{
              margin: "6px 0 0 0",
              fontSize: 26,
              fontWeight: 800,
              letterSpacing: -0.5,
              color: "var(--text)",
            }}
          >
            {tab?.label ?? tabId}
          </h1>
        </div>
        {updated ? (
          <span
            title={updated.title}
            style={{
              marginLeft: "auto",
              fontFamily: "var(--font-mono)",
              fontVariantNumeric: "tabular-nums",
              fontSize: 11,
              color: "var(--text-2)",
              background: "var(--panel)",
              border: "1px solid var(--border)",
              padding: "5px 10px",
              borderRadius: 6,
              whiteSpace: "nowrap",
            }}
          >
            Updated @ <b style={{ color: "var(--text)" }}>{updated.label}</b>
          </span>
        ) : null}
      </header>
      <DashboardFilterBar divisions={manifest.divisions} services={manifest.enabled_services} />
      {widgets.length === 0 ? (
        <EmptyState title="No widgets configured for this tab." />
      ) : (
        <>
          {/* MER-162: group widgets into within-tab sections parsed
              from widget.id (L1 / L2a / L2b / ...). MER-536: each section
              is a vertical stack of its authored grid.rows, and every row
              renders its own sub-grid below so a row that does not fill the
              width can never let the next row's tiles backfill its gap (the
              old single `row dense` grid ignored grid.row and left KPI rows
              short of full width). Preserves manifest widget order. */}
          {Array.from(groupBySection(widgets, tabId).entries()).map(
            ([sectionKey, sectionWidgets]) => {
              // MER-178: only render the section header when an authored
              // human label exists. Falling back to the section key would
              // leak raw IDs like "1.2" / "L2a" onto the client UI.
              const label =
                sectionKey === "Other" ? null : sectionLabel(tabId, sectionKey, sectionWidgets);
              const widgetById = new Map(sectionWidgets.map((w) => [w.id, w]));
              // MER-536: lay the section out as authored rows. A row that is
              // entirely single-number KPI cards fills the width (3 -> span 4,
              // 2 -> span 6); everything else keeps its breakpoint-scaled span.
              const rows = planSectionRows(
                sectionWidgets.map((w) => ({
                  id: w.id,
                  row: w.grid?.row,
                  isSingleNumber: effectiveChartType(w) === "single_number",
                  authoredSpan: Math.max(1, Math.min(12, w.grid?.span ?? 3)),
                })),
                gridCols,
              );
              return (
                <section
                  key={sectionKey}
                  // MER-536: vertical stack of header + one sub-grid per row.
                  style={{ display: "grid", gap: 16, marginBottom: 28 }}
                >
                  {label ? (
                    <SectionHeader
                      sectionKey={sectionKey}
                      label={label}
                      domain={widgetDomain(sectionWidgets[0])}
                      provenance={sectionProvenance(sectionWidgets)}
                    />
                  ) : null}
                  {rows.map((row, rowIdx) => (
                    <div
                      key={`row-${rowIdx}`}
                      style={{
                        // MER-528: track count reflows with the viewport instead
                        // of a hardcoded 12 that just squeezes on narrow laptops.
                        display: "grid",
                        gap: 16,
                        gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))`,
                      }}
                    >
                      {row.items.map(({ id, span }) => {
                        const widget = widgetById.get(id);
                        if (!widget) return null;
                        const isFocused = focusWidgetId === id;
                        return (
                          <div
                            key={id}
                            // MER-312: tag every widget wrapper with its manifest
                            // id so the filter-dimension audit harness (Playwright)
                            // can locate the rendered tile, capture its displayed
                            // value, and verify it changes when the LOB / period /
                            // range / division selectors flip. Invisible to
                            // customers (no styling, no aria), a no-op outside tests.
                            data-widget-id={id}
                            data-widget-tab={tabId ?? ""}
                            // MER-625: the tile a drill landed on. The attribute
                            // is what the audit harness and the drill tests assert
                            // against; the ring is what tells the reader which of
                            // the 42 tiles answers the one they clicked. tabIndex
                            // -1 makes it programmatically focusable without
                            // adding a tab stop for everyone else.
                            data-widget-focus={isFocused ? "true" : undefined}
                            tabIndex={isFocused ? -1 : undefined}
                            // MER-528: minWidth:0 lets a tile shrink inside its
                            // track; overflow:auto keeps a too-wide table/chart
                            // scrolling INSIDE the tile rather than pushing the page
                            // into horizontal scroll.
                            style={{
                              gridColumn: `span ${span} / span ${span}`,
                              minWidth: 0,
                              overflow: "auto",
                              ...(isFocused
                                ? {
                                    outline: "2px solid var(--brand-teal)",
                                    outlineOffset: 3,
                                    borderRadius: 12,
                                  }
                                : null),
                            }}
                          >
                            {effectiveChartType(widget) === "ro_efficiency" ? (
                              // RO Efficiency tab: a tracker panel reads its own
                              // work order slice and draws its own table.
                              <RoEfficiencyPanel
                                widget={widget}
                                divisionOrder={manifest.divisions ?? []}
                              />
                            ) : widget.type === "container_inventory" &&
                              (widget.status ?? "ok") === "ok" ? (
                              // Containers tab: a live container_inventory widget has
                              // its own renderer (one grouped read shared by the tab).
                              // A blocked one still takes the standard awaiting path.
                              <ContainerInventoryWidget widget={widget} />
                            ) : (
                              <WidgetRenderer
                                widget={widget}
                                onSelect={openWidget}
                                drillLabel={
                                  drillTargets.has(id)
                                    ? `Break down in ${drillTargets.get(id)?.tab}`
                                    : undefined
                                }
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </section>
              );
            },
          )}
          {/* MER-588: the rates panel is an INPUT surface, not a metric, so it
              sits below the tab's widgets rather than inside the manifest grid.
              Cost & Disposal is where it belongs: these are disposal rates, and
              MER-546's port has this tab absorbing the Disposal Tracker v11 --
              which is exactly where Zay maintains them today.

              MER-585 shipped the component, the client and migration 0050 but
              never mounted it, so it was unreachable. Built and usable are not
              the same thing. */}
          {tabId === RATE_PANEL_TAB ? (
            <div style={{ marginTop: 8 }}>
              <RateInputPanel tenantId={tenantId} />
            </div>
          ) : null}
        </>
      )}
      {drillWidget ? (
        <DrillDownModal widget={drillWidget} open={true} onClose={() => setDrillWidget(null)} />
      ) : null}
    </main>
  );
}

/**
 * MER-688: frame shared by the tier lock and the staff-only notice: the
 * card, then a link back to the first tab a client can actually view (the
 * first manifest tab that is neither staff_only nor tier-locked; the first
 * wave 1 tab for every current template).
 */
function LockedTabPage(props: {
  manifest: DashboardManifest;
  slug: string | undefined;
  children: ReactNode;
}) {
  const { manifest, slug } = props;
  const navigate = useNavigate();
  return (
    <main style={{ padding: 32 }}>
      {props.children}
      <p style={{ marginTop: 16, fontSize: 12, color: "var(--text-muted)" }}>
        <a
          href="#back"
          onClick={(ev) => {
            ev.preventDefault();
            const first = firstClientTab(manifest);
            if (first && slug) navigate(`/${slug}/${first}`, { replace: true });
          }}
        >
          Return to a tab you can view
        </a>
      </p>
    </main>
  );
}

function firstClientTab(manifest: DashboardManifest): string | undefined {
  // Both gates, so the link never lands on the staff-only wall or on
  // another tier lock. Undefined (link is inert) only for a manifest with
  // no tab a client may open, which no current template produces.
  const client = (manifest.tabs ?? []).find(
    (t) =>
      t.staff_only !== true &&
      tabIsUnlocked(t, manifest.tier) &&
      (t.id ?? t.label ?? "").length > 0,
  );
  return client ? (client.id ?? client.label) : undefined;
}

/**
 * Wave 1 release (AJ 2026-09-28): the bare /<slug> redirect. Lands on the
 * Executive page when the tenant has the workbook (the same exec_kpi probe
 * that puts the Executive link first in the nav), else the first tab the
 * session may see. Waits on the probe (8s timeout, then "error") so a
 * client does not flash through another tab first. A session with no
 * tenant id never gets a probe answer, so it skips the wait.
 */
function IndexRedirect(props: {
  tabs: DashboardManifest["tabs"];
  preferred: string | null | undefined;
  isStaff: boolean;
  hasTenant: boolean;
  derivedExecutive?: boolean;
}) {
  const exec = useExecKpiAvailability();
  const target = resolveIndexRoute(
    props.tabs,
    props.preferred,
    props.hasTenant ? exec : "missing",
    { isStaff: props.isStaff, derivedExecutive: props.derivedExecutive === true },
  );
  if (target === null) return <Skeleton label="Loading dashboard" height={120} />;
  if (!target) return <FallbackNotice message="Manifest has no tabs." />;
  return <Navigate to={target} replace />;
}

function FallbackNotice(props: { message: string }) {
  return (
    <main
      style={{
        padding: "48px 32px",
        display: "grid",
        placeItems: "center",
        minHeight: "60vh",
      }}
    >
      <div
        style={{
          background: "var(--bg-card)",
          border: "1px dashed var(--border-subtle)",
          borderRadius: 14,
          padding: "32px 40px",
          textAlign: "center",
          maxWidth: 520,
        }}
      >
        <div
          style={{
            fontSize: 12,
            textTransform: "uppercase",
            letterSpacing: 0.5,
            color: "var(--text-muted)",
          }}
        >
          Dashboard
        </div>
        <p style={{ marginTop: 12, color: "var(--text-muted)" }}>{props.message}</p>
      </div>
    </main>
  );
}

/**
 * MER-214: rendered when the slug has no bundled manifest (Cloudflare
 * Pages serves index.html as a 404 fallback or returns a real 404).
 * New customer onboarding lands here before the manifest is authored;
 * the card replaces the prior raw "Manifest YAML did not parse to an
 * object" error message.
 */
function NotConfiguredCard(props: { slug: string }) {
  return (
    <main
      style={{
        display: "grid",
        placeItems: "center",
        minHeight: "100vh",
        padding: "32px",
        background:
          "radial-gradient(at 30% 20%, rgba(38,168,159,0.06), transparent 50%), var(--bg)",
      }}
    >
      <div
        style={{
          background: "var(--bg-card)",
          border: "1px solid var(--border-subtle)",
          borderRadius: 16,
          padding: "36px 36px 28px",
          width: "100%",
          maxWidth: 460,
          boxShadow: "var(--shadow-card)",
          display: "grid",
          gap: 18,
          textAlign: "center",
        }}
      >
        <div style={{ display: "grid", justifyItems: "center", gap: 14 }}>
          <MeridianLockup size="lg" align="stack" />
        </div>
        <div style={{ display: "grid", gap: 8 }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: "var(--brand-deep)" }}>
            Dashboard not configured yet
          </div>
          <p style={{ fontSize: 14, color: "var(--text-2)", lineHeight: 1.5 }}>
            We have your account set up but the dashboard for{" "}
            <strong style={{ color: "var(--text)" }}>{props.slug}</strong> isn&apos;t live yet. Your
            account manager will let you know as soon as it&apos;s ready.
          </p>
        </div>
        <div
          style={{
            borderTop: "1px solid var(--border-subtle)",
            marginTop: 6,
            paddingTop: 12,
          }}
        >
          <p style={{ fontSize: 12, color: "var(--text-muted)" }}>
            Need help?{" "}
            <a
              href="mailto:support@meridiananalytics.io"
              style={{ color: "var(--brand-deep)", textDecoration: "none" }}
            >
              support@meridiananalytics.io
            </a>
          </p>
        </div>
      </div>
    </main>
  );
}

/**
 * MER-214: rendered when the manifest fetch succeeds but parsing
 * fails, or the network request itself fails. Customer sees a
 * brand-aligned card with a support link; the raw parse error stays
 * in the browser console for engineering.
 */
function ConfigurationErrorCard(props: { slug: string }) {
  return (
    <main
      style={{
        display: "grid",
        placeItems: "center",
        minHeight: "100vh",
        padding: "32px",
        background:
          "radial-gradient(at 30% 20%, rgba(38,168,159,0.06), transparent 50%), var(--bg)",
      }}
    >
      <div
        style={{
          background: "var(--bg-card)",
          border: "1px solid var(--border-subtle)",
          borderRadius: 16,
          padding: "36px 36px 28px",
          width: "100%",
          maxWidth: 460,
          boxShadow: "var(--shadow-card)",
          display: "grid",
          gap: 18,
          textAlign: "center",
        }}
      >
        <div style={{ display: "grid", justifyItems: "center", gap: 14 }}>
          <MeridianLockup size="lg" align="stack" />
        </div>
        <div style={{ display: "grid", gap: 8 }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: "var(--brand-deep)" }}>
            Dashboard configuration error
          </div>
          <p style={{ fontSize: 14, color: "var(--text-2)", lineHeight: 1.5 }}>
            Something is off with the dashboard for{" "}
            <strong style={{ color: "var(--text)" }}>{props.slug}</strong>. We&apos;ve been
            notified. Refresh the page in a few minutes, and reach out if it persists.
          </p>
        </div>
        <div
          style={{
            borderTop: "1px solid var(--border-subtle)",
            marginTop: 6,
            paddingTop: 12,
          }}
        >
          <p style={{ fontSize: 12, color: "var(--text-muted)" }}>
            Need help?{" "}
            <a
              href="mailto:support@meridiananalytics.io"
              style={{ color: "var(--brand-deep)", textDecoration: "none" }}
            >
              support@meridiananalytics.io
            </a>
          </p>
        </div>
      </div>
    </main>
  );
}
